import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { FiPrWordmark } from '../components/FiPrLogo';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0D0F14' }}>
      <div
        className="w-full max-w-sm rounded-xl p-8"
        style={{ background: '#13151C', border: '1px solid #1E2028' }}
        data-testid="login-form"
      >
        <div className="flex justify-center mb-6">
          <FiPrWordmark iconSize={44} textSize="3xl" />
        </div>
        <h1 className="text-2xl font-semibold text-white text-center mb-1 tracking-tight">Welcome back</h1>
        <p className="text-[#6C7086] text-sm text-center mb-7">Sign in to FiPr.ai</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#A0A4B8] uppercase tracking-wider mb-1.5 block">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6C7086]" strokeWidth={1.5} />
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-9 text-sm"
                style={{ background: '#0D0F14', borderColor: '#1E2028', color: '#FFFFFF' }}
                placeholder="your@email.com"
                required
                data-testid="email-input"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-[#A0A4B8] uppercase tracking-wider mb-1.5 block">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6C7086]" strokeWidth={1.5} />
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="pl-9 text-sm"
                style={{ background: '#0D0F14', borderColor: '#1E2028', color: '#FFFFFF' }}
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#F5A623] hover:bg-[#E0961B] text-black font-semibold py-5 transition-all duration-200"
            data-testid="login-submit-btn"
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        {/* ── or-divider + Google OAuth ─────────────────────────────────── */}
        <div className="flex items-center gap-3 my-5" aria-hidden="true">
          <div className="flex-1 h-px bg-[#1E2028]" />
          <span className="text-xs text-[#6C7086] uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-[#1E2028]" />
        </div>
        <Button
          type="button"
          onClick={() => {
            const cb = encodeURIComponent(`${window.location.origin}/`);
            window.location.href = `https://auth.emergentagent.com/?redirect=${cb}`;
          }}
          variant="outline"
          className="w-full bg-white hover:bg-gray-100 text-gray-800 border border-gray-300 font-medium py-5 rounded-lg flex items-center justify-center gap-3"
          data-testid="continue-with-google-btn"
        >
          <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
          </svg>
          Continue with Google
        </Button>

        <div className="mt-6 text-center">
          <p className="text-[#6C7086] text-sm">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#F5A623] hover:text-[#E0961B] font-medium transition-colors" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-[#6C7086] hover:text-white text-sm transition-colors" data-testid="back-home-link">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
